(() => {
  'use strict';
  const root = document.querySelector('[data-calvielle-care]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const api = 'https://calvielle-care.smfwzc.workers.dev';
  const storageKey = 'calvielle.care.session.v1';
  const el = name => root.querySelector(`[data-care-${name}]`);
  const fields = Object.fromEntries(['presence','availability','welcome','welcome-label','start','start-panel','chat','messages','queue','composer','send','join','end','restart','feedback'].map(name => [name, el(name)]));
  const input = root.querySelector('textarea');
  const panel = el('panel'), launcher = el('launcher'), closeButton = el('close');
  const thread = el('thread'), options = el('options'), optionsToggle = el('options-toggle'), expandButton = el('expand'), backdrop = el('backdrop');
  const mobile = matchMedia('(max-width: 749px)');
  const background = new Map();
  let scrollLock = null;
  let resizeFollowEnd = false;
  let returnFocus = launcher, closeTimer;
  let credentials = null, current = null, available = false, reachable = false, busy = false, polling = false;
  let pending = null, stopped = false;
  const rendered = new Set();
  const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (saved && uuid.test(saved.sessionId) && typeof saved.token === 'string' && saved.token.length <= 256 && Date.parse(saved.expiresAt) > Date.now()) credentials = saved;
    else sessionStorage.removeItem(storageKey);
  } catch { /* A denied storage permission must not prevent an in-memory chat. */ }

  function feedback(text = '') { fields.feedback.textContent = text; }
  function setOptions(open, focus = false) {
    options.hidden = !open;
    optionsToggle.setAttribute('aria-expanded', String(open));
    if (focus) {
      const target = open ? options : optionsToggle;
      target?.focus();
    }
  }
  function fitInput() {
    if (fields.composer.hidden) return;
    input.style.height = '40px';
    input.style.height = `${Math.min(112, Math.max(40, input.scrollHeight))}px`;
  }
  function syncViewport() {
    const viewport = window.visualViewport;
    root.style.setProperty('--care-viewport-height', `${viewport?.height || window.innerHeight}px`);
    root.style.setProperty('--care-viewport-top', `${viewport?.offsetTop || 0}px`);
  }
  function syncModal() {
    const modal = mobile.matches && !panel.hidden;
    panel.setAttribute('aria-modal', String(modal));
    backdrop.hidden = !modal;
    if (modal && !scrollLock) {
      scrollLock = [document.documentElement.style.overflow, document.body.style.overflow];
      document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden';
      // Inert only siblings outside this widget, preserving any pre-existing state.
      for (let node = root; node.parentElement; node = node.parentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling === node || ['SCRIPT','STYLE','LINK','HEAD'].includes(sibling.tagName)) continue;
          background.set(sibling, sibling.inert); sibling.inert = true;
        }
        if (node.parentElement === document.body) break;
      }
    } else if (!modal && scrollLock) {
      for (const [node, inert] of background) node.inert = inert;
      background.clear();
      [document.documentElement.style.overflow, document.body.style.overflow] = scrollLock;
      scrollLock = null;
    }
  }
  function openPanel(trigger = launcher) {
    clearTimeout(closeTimer); panel.classList.remove('is-closing');
    returnFocus = trigger; panel.hidden = false; launcher.setAttribute('aria-expanded','true');
    root.classList.add('is-open'); syncViewport(); syncModal(); fitInput();
    closeButton.focus(); refresh();
  }
  function closePanel() {
    setOptions(false);
    launcher.setAttribute('aria-expanded','false');
    const finish = () => { panel.hidden = true; panel.classList.remove('is-closing'); root.classList.remove('is-open'); syncModal(); (returnFocus?.isConnected ? returnFocus : launcher).focus(); };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else { panel.classList.add('is-closing'); closeTimer = setTimeout(finish,200); }
  }
  function remember() {
    try { if (credentials) sessionStorage.setItem(storageKey, JSON.stringify(credentials)); else sessionStorage.removeItem(storageKey); } catch { /* Session remains in memory only. */ }
  }
  function controls() {
    const closed = current?.session.status === 'closed';
    fields.start.disabled = busy || !reachable;
    fields.send.disabled = busy || !current || closed || !reachable || !input.value.trim();
    input.disabled = !current || closed || !reachable;
    input.readOnly = busy;
    fields.join.hidden = !current || ['queued','active','closed'].includes(current.session.status);
    fields.join.disabled = busy || !available;
    fields.end.hidden = !current || closed;
    fields.end.disabled = busy;
    fields.restart.hidden = !closed;
    fields.restart.disabled = busy;
    fields.composer.hidden = !current || !!closed;
    fields.queue.hidden = !current || current.session.status === 'assistant';
    if (current && reachable) fields.availability.hidden = true;
    if (fields.welcome) fields.welcome.hidden = !!current && (current.messages.length > 0 || current.session.status !== 'assistant');
    fields.start.textContent = busy && !current ? 'Connecting…' : 'Start conversation →';
    fields.send.setAttribute('aria-label', busy ? 'Sending message' : 'Send message');
    fields.send.dataset.busy = String(busy);
    fields.composer.setAttribute('aria-busy', String(busy));
  }
  const errorMessages = {
    offline:'Our studio has just gone offline. You can still email us.',
    human_offline:'Our studio has just gone offline. You can still email us.',
    queue_full:'The studio queue is full at the moment. Please email us or try again later.',
    rate_limited:'Please pause a moment before trying again. Our email form is also available.',
    session_expired:'This conversation has expired. Please start a new conversation.',
    invalid_session:'This conversation is no longer available. Please start again.',
    conflict:'This conversation has changed. Please refresh its status and try again.',
    feature_disabled:'Chat is temporarily unavailable. Please email our studio.'
  };
  async function request(path, body, authenticated = false) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 22000);
    try {
      const headers = { Accept:'application/json' };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      if (authenticated) {
        if (!credentials) throw new Error('This conversation has expired. Please start again.');
        headers.Authorization = `Bearer ${credentials.token}`;
      }
      const response = await fetch(api + path, { method: body === undefined ? 'GET' : 'POST', headers, body: body === undefined ? undefined : JSON.stringify(body), signal:controller.signal, credentials:'omit', cache:'no-store', redirect:'error' });
      const data = await response.json();
      if (!response.ok) {
        if (authenticated && [401,403,404,410].includes(response.status)) {
          credentials = null; remember();
          if (current) { current.session.status = 'closed'; render(current); }
        }
        throw new Error(errorMessages[data?.error?.code] || 'We could not complete that request. Please try again, or send an email below.');
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error('Connection interrupted. Please try again. The email form remains available.');
      throw error;
    } finally { clearTimeout(timeout); }
  }
  function safeLink(value) {
    try {
      const url = new URL(value);
      if (url.username || url.password || url.protocol !== 'https:' || url.port || url.search) return null;
      if (url.origin === 'https://calvielle.com' && /^\/(?:$|collections\/(?:all|bracelets|the-elemental-five)$|pages\/contact$|policies\/(?:shipping-policy|refund-policy|privacy-policy|terms-of-service)$)/.test(url.pathname)) return url.href;
      if (url.origin === 'https://account.calvielle.com' && /^\/account\/?$/.test(url.pathname)) return url.href;
    } catch { /* Only approved navigation, never arbitrary model URLs. */ }
    return null;
  }
  function render(snapshot) {
    if (!snapshot?.session || !Array.isArray(snapshot.messages)) throw new Error('Chat is temporarily unavailable. Please use the email form.');
    current = snapshot;
    fields['start-panel'].hidden = true;
    fields.chat.hidden = false;
    const session = snapshot.session;
    if (session.status === 'queued') {
      const position = Number.isInteger(session.queuePosition) && session.queuePosition > 0 ? `Your place in the queue: ${session.queuePosition}. ` : '';
      const estimate = Number.isFinite(session.estimatedWaitMinutes) && session.estimatedWaitMinutes > 0 ? `Estimated wait: about ${Math.ceil(session.estimatedWaitMinutes)} minutes. This may change.` : 'An estimate is not yet available.';
      fields.queue.textContent = session.agentOnline ? position + estimate : 'Our studio is currently away. You can leave this conversation and email us below.';
    } else if (session.status === 'active') fields.queue.textContent = session.agentOnline ? 'You are speaking with the Calvielle studio.' : 'The studio connection is currently unavailable. Your conversation is saved; email is also available below.';
    else if (session.status === 'closed') fields.queue.textContent = 'This conversation has ended. Thank you for getting in touch.';
    else fields.queue.textContent = '';
    const messages = fields.messages;
    const scroller = thread;
    const atEnd = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 100;
    let added = false;
    for (const item of snapshot.messages.slice(0,100)) {
      if (!item.id || rendered.has(item.id)) continue;
      rendered.add(item.id); added = true;
      const row = document.createElement('li'); row.className = 'cv-care__message';
      const role = ['visitor','assistant','agent','system'].includes(item.role) ? item.role : 'system';
      row.dataset.role = role;
      const label = document.createElement('p'); label.className = 'cv-care__message-label';
      label.textContent = {visitor:'You',assistant:'Calvielle · AI',agent:'Calvielle · Studio',system:'Update'}[role];
      const avatar = document.createElement('span'); avatar.className = 'cv-care__avatar'; avatar.dataset.kind = role; avatar.setAttribute('aria-hidden','true');
      if (role === 'visitor') {
        const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
        svg.setAttribute('viewBox','0 0 24 24'); svg.setAttribute('width','18'); svg.setAttribute('height','18'); svg.setAttribute('fill','none');
        const path = document.createElementNS('http://www.w3.org/2000/svg','path');
        path.setAttribute('d','M15.5 8a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM5.5 20v-1.5a6.5 6.5 0 0 1 13 0V20');
        path.setAttribute('stroke','currentColor'); path.setAttribute('stroke-width','1.2'); path.setAttribute('stroke-linecap','round'); svg.append(path); avatar.append(svg);
      } else avatar.textContent = role === 'system' ? 'i' : 'C';
      const content = document.createElement('div'); content.className = 'cv-care__message-content';
      const text = document.createElement('p'); text.className = 'cv-care__message-text'; text.textContent = String(item.text || '').slice(0,4000);
      content.append(label,text); row.append(avatar,content);
      if (Array.isArray(item.links)) {
        const links = document.createElement('div'); links.className = 'cv-care__message-links';
        for (const itemLink of item.links.slice(0,4)) {
          const href = safeLink(itemLink.url); if (!href) continue;
          const link = document.createElement('a'); link.className = 'cv-care__link'; link.href = href; link.textContent = String(itemLink.label || 'Learn more').slice(0,80); links.append(link);
        }
        content.append(links);
      }
      messages.append(row);
    }
    controls();
    fitInput();
    if (added && atEnd) requestAnimationFrame(() => { scroller.scrollTop = scroller.scrollHeight; });
  }
  async function status() {
    try {
      const data = await request('/api/public/status');
      available = data.humanOnline === true && data.queueOpen === true;
      reachable = data.aiAvailable === true || available;
      fields.presence.textContent = data.humanOnline === true ? 'Studio online' : 'Studio offline';
      fields.presence.dataset.online = String(data.humanOnline === true);
      fields.availability.textContent = available ? 'AI guidance, or join our studio’s queue.' : reachable ? 'AI help is available.' : 'Chat is unavailable. Please email our studio.';
      fields.availability.hidden = !!current && reachable;
      if (fields['welcome-label']) fields['welcome-label'].textContent = data.aiAvailable === true ? 'Calvielle · AI' : 'Calvielle · Studio';
    } catch {
      available = false; reachable = false;
      fields.presence.textContent = 'Unable to connect'; fields.presence.dataset.online = 'false';
      fields.availability.hidden = false;
      fields.availability.textContent = 'Please email our studio instead.';
    }
    controls();
  }
  async function refresh() {
    if (busy || polling || stopped || document.hidden || panel.hidden) return;
    polling = true;
    try {
      await status();
      if (credentials && current?.session.status !== 'closed') render(await request(`/api/public/sessions/${credentials.sessionId}`, undefined, true));
    } catch (error) { feedback(error.message); }
    finally { polling = false; }
  }
  async function start() {
    if (busy || !reachable) return;
    busy = true; feedback(); controls();
    try {
      credentials = await request('/api/public/sessions', {consent:true}); remember();
      rendered.clear(); fields.messages.replaceChildren();
      render(await request(`/api/public/sessions/${credentials.sessionId}`, undefined, true));
      if (!panel.hidden) input.focus();
    } catch (error) { feedback(error.message); }
    finally { busy = false; controls(); if (!panel.hidden && current?.session.status === 'assistant') input.focus(); }
  }
  async function mutate(action, payload = {}) {
    if (busy || !credentials) return false;
    busy = true; feedback(); controls();
    const fingerprint = action + JSON.stringify(payload);
    if (pending?.fingerprint !== fingerprint) pending = {fingerprint,requestId:crypto.randomUUID()};
    try {
      const result = await request(`/api/public/sessions/${credentials.sessionId}/${action}`, {...payload,requestId:pending.requestId}, true);
      pending = null; render(result); return true;
    } catch (error) { feedback(error.message); return false; }
    finally { busy = false; controls(); }
  }
  fields.start.addEventListener('click',start);
  el('disclosure')?.addEventListener('click',() => {
    const button = el('disclosure'), details = el('disclosure-panel');
    if (!details) return;
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded',String(expanded));
    details.setAttribute('aria-hidden',String(!expanded)); details.inert = !expanded;
  });
  launcher.addEventListener('click',() => { if (panel.hidden || panel.classList.contains('is-closing')) openPanel(); else closePanel(); });
  closeButton.addEventListener('click',closePanel);
  backdrop.addEventListener('click', closePanel);
  optionsToggle.addEventListener('click', () => setOptions(options.hidden, true));
  expandButton.addEventListener('click', () => {
    resizeFollowEnd = thread.scrollHeight - thread.scrollTop - thread.clientHeight < 100;
    const expanded = panel.classList.toggle('is-expanded');
    expandButton.setAttribute('aria-pressed', String(expanded));
    expandButton.setAttribute('aria-label', expanded ? 'Restore conversation size' : 'Expand conversation');
    fitInput();
  });
  panel.addEventListener('transitionend', event => {
    if (event.target !== panel || !['width','height'].includes(event.propertyName)) return;
    fitInput();
    if (resizeFollowEnd) thread.scrollTop = thread.scrollHeight;
  });
  root.addEventListener('keydown',event => {
    if (panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (!options.hidden) setOptions(false, true); else closePanel();
    }
    if (event.key === 'Tab' && mobile.matches) {
      const focusable = [...panel.querySelectorAll('button,a[href],textarea,[tabindex="0"]')].filter(item => !item.disabled && !item.closest('[inert]') && item.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  });
  document.addEventListener('click',event => {
    const trigger = event.target.closest?.('[data-care-open]');
    if (trigger) { event.preventDefault(); openPanel(trigger); }
    if (!options.hidden && !options.contains(event.target) && !optionsToggle.contains(event.target)) setOptions(false);
    // Close the mobile overlay before native in-page email navigation.
    const email = event.target.closest?.('a[href="/pages/contact#email-calvielle"]');
    if (email && root.contains(email)) closePanel();
  });
  input.addEventListener('input', () => { fitInput(); controls(); });
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && event.keyCode !== 229 && !mobile.matches) {
      event.preventDefault();
      if (!fields.send.disabled) fields.composer.requestSubmit();
    }
  });
  fields.composer.addEventListener('submit',async event => {
    event.preventDefault();
    const text = input.value.trim(); if (!text || !current || fields.send.disabled) return;
    thread.scrollTop = thread.scrollHeight;
    if (await mutate('messages',{text,mode:current.session.status === 'assistant' ? 'assistant' : 'human'})) input.value = '';
    fitInput(); controls();
    if (!panel.hidden) input.focus();
  });
  fields.join.addEventListener('click',async () => { if (available) { setOptions(false); await mutate('join'); if (!panel.hidden) input.focus(); } });
  fields.end.addEventListener('click',async () => { setOptions(false); await mutate('leave'); if (!panel.hidden) optionsToggle.focus(); });
  fields.restart.addEventListener('click',() => {
    credentials = null; current = null; pending = null; remember(); rendered.clear(); fields.messages.replaceChildren();
    input.value = ''; setOptions(false); fields.queue.textContent = '';
    fields.chat.hidden = true; fields['start-panel'].hidden = false; feedback(); controls(); fields.start.focus();
  });
  mobile.addEventListener('change', () => { syncModal(); fitInput(); });
  window.addEventListener('resize', () => { syncViewport(); fitInput(); });
  window.visualViewport?.addEventListener('resize', syncViewport);
  window.visualViewport?.addEventListener('scroll', syncViewport);
  document.addEventListener('visibilitychange',() => { if (!document.hidden) refresh(); });
  window.addEventListener('pagehide',() => { stopped = true; });
  window.addEventListener('pageshow',() => { stopped = false; refresh(); });
  setInterval(() => { if (!document.hidden && !stopped && !panel.hidden) refresh(); }, 20000);
  refresh();
})();
